/**
 * PublicSharePage — Page publique d'accès à un lien RGPD signé.
 *
 * Flow :
 *  1. À l'arrivée : GET /api/rgpd/public-link/info/{token} → métadonnées
 *     (titre, expéditeur, type de ressource, date d'expiration)
 *  2. L'utilisateur consulte le récap RGPD (consentement implicite à l'accès)
 *  3. Click sur "Accéder à la ressource" → POST /api/rgpd/public-link/access/{token}
 *     → enregistre l'access log (IP + UA) côté backend
 *  4. Le frontend récupère {resource_type, resource_id} et affiche la ressource
 *     correspondante :
 *       - document → preview PDF embed via /api/documents/{id}/preview-public
 *       - pdf      → idem
 *       - autre    → message "type non géré" + lien direct
 *
 * Route publique enregistrée dans AppRoutes.js — AUCUNE auth requise.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Calendar, User, FileText, AlertTriangle, CheckCircle2,
  Loader2, ExternalLink, Download, Lock, Eye, MessageSquare, Send,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function formatDateFR(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }); } catch { return iso; }
}

const RESOURCE_TYPE_LABELS = {
  document: 'Document',
  pdf: 'Document PDF',
  invoice: 'Facture',
  devis: 'Devis',
};

export default function PublicSharePage() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | info | error | accessed | expired
  const [info, setInfo] = useState(null);
  const [accessData, setAccessData] = useState(null);
  const [accessing, setAccessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Phase 1 : récupérer les métadonnées
  useEffect(() => {
    if (!token) { setPhase('error'); setErrorMsg('Token manquant'); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/rgpd/public-link/info/${encodeURIComponent(token)}`);
        if (!cancelled) { setInfo(res.data); setPhase('info'); }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        const detail = err.response?.data?.detail || 'Erreur de chargement';
        if (status === 410) { setPhase('expired'); setErrorMsg(detail); }
        else { setPhase('error'); setErrorMsg(detail); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Phase 2 : enregistrer l'accès
  const grantAccess = async () => {
    setAccessing(true);
    try {
      const res = await axios.post(`${API}/rgpd/public-link/access/${encodeURIComponent(token)}`);
      setAccessData(res.data);
      setPhase('accessed');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Impossible d\'accéder à la ressource';
      setErrorMsg(detail);
      if (err.response?.status === 410) setPhase('expired');
    } finally {
      setAccessing(false);
    }
  };

  // ============ RENDER ============

  const Brand = (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
        <Shield className="h-5 w-5 text-white" />
      </div>
      <div>
        <div className="text-xs font-bold tracking-wider text-amber-700">O'SCOP OUTREMER</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Partage sécurisé RGPD</div>
      </div>
    </div>
  );

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-6" data-testid="public-share-loading">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto mb-3" />
          <p className="text-sm text-slate-600">Vérification du lien…</p>
        </div>
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-rose-50 flex items-center justify-center p-6" data-testid="public-share-expired">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="h-14 w-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-7 w-7 text-rose-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Lien expiré ou révoqué</h1>
            <p className="text-sm text-slate-600 mb-4">{errorMsg}</p>
            <p className="text-xs text-slate-400">
              Ce lien public n'est plus valide. Veuillez contacter l'expéditeur pour obtenir un nouveau lien.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 flex items-center justify-center p-6" data-testid="public-share-error">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900">Erreur</h1>
          <p className="text-sm text-slate-600 mt-2">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === 'accessed' && accessData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 p-6" data-testid="public-share-accessed">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-5 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-100">Accès accordé</div>
                  <div className="font-bold">{info?.title || 'Ressource partagée'}</div>
                </div>
              </div>
              {Brand}
            </div>
            <div className="p-6">
              <PublicResourceViewer
                resourceType={accessData.resource_type}
                resourceId={accessData.resource_id}
                token={token}
              />
            </div>
            <div className="bg-slate-50 border-t border-slate-200 p-4 text-[11px] text-slate-500 text-center">
              <Shield className="inline h-3 w-3 mr-1" />
              Cet accès a été enregistré (date, IP) conformément à l'article 32 du RGPD.
            </div>
          </div>

          {/* Mini-portail destinataire — formulaire de réponse */}
          {info?.allow_replies && (
            <ReplyForm token={token} senderEmailHint={info?.issued_by} />
          )}
        </div>
      </div>
    );
  }

  // phase === 'info' — Page de consentement
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-6" data-testid="public-share-info">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-lg w-full">
        <div className="bg-gradient-to-br from-indigo-950 to-slate-900 p-6 text-white">
          {Brand}
          <h1 className="text-2xl font-bold mt-5">Vous avez reçu un partage sécurisé</h1>
          <p className="text-sm text-indigo-200 mt-1">Lien à usage unique enregistré au registre RGPD.</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <FileText className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Ressource</div>
                <div className="font-semibold text-slate-900" data-testid="public-share-title">
                  {info?.title || RESOURCE_TYPE_LABELS[info?.resource_type] || 'Document partagé'}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{RESOURCE_TYPE_LABELS[info?.resource_type] || info?.resource_type}</div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <User className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Expéditeur</div>
                <div className="font-semibold text-slate-900" data-testid="public-share-sender">{info?.issued_by || '—'}</div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <Calendar className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-700">Expire le</div>
                <div className="font-semibold text-amber-900" data-testid="public-share-expiry">{formatDateFR(info?.expires_at)}</div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4 text-xs text-slate-500 leading-relaxed">
            En cliquant sur <strong className="text-slate-700">Accéder à la ressource</strong>, vous acceptez que votre accès
            (date, IP, navigateur) soit enregistré dans le registre RGPD du site, conformément à l'article 32.
            Aucun compte n'est créé. Aucune donnée additionnelle n'est collectée.
          </div>

          <button
            type="button"
            onClick={grantAccess}
            disabled={accessing}
            className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-semibold py-3 px-4 rounded-lg shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="public-share-grant-btn"
          >
            {accessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            {accessing ? 'Enregistrement…' : 'Accéder à la ressource'}
          </button>

          <div className="text-center text-[10px] text-slate-400">
            <Link to="/" className="hover:text-indigo-600">← Retour à l'accueil</Link>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * PublicResourceViewer — Affiche la ressource selon son type.
 *
 * Volontairement minimaliste pour Phase 3 : on affiche un lien direct vers
 * la ressource avec l'API publique adéquate. Les endpoints publics dédiés
 * (ex: GET /api/public/document/{id}?token=...) peuvent être ajoutés en
 * Phase 4 sans toucher au registre RGPD.
 */
function PublicResourceViewer({ resourceType, resourceId, token }) {
  const handleOpen = () => {
    // Construire une URL publique vers la ressource
    const url = `${API}/public/${resourceType}/${encodeURIComponent(resourceId)}?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="text-center space-y-4 py-4">
      <div className="inline-flex h-16 w-16 rounded-xl bg-indigo-100 items-center justify-center">
        <FileText className="h-8 w-8 text-indigo-600" />
      </div>
      <div>
        <div className="text-lg font-bold text-slate-900">{RESOURCE_TYPE_LABELS[resourceType] || resourceType}</div>
        <div className="text-xs text-slate-500 font-mono mt-1" data-testid="public-resource-id">{resourceId}</div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 justify-center">
        <button
          onClick={handleOpen}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-semibold"
          data-testid="public-resource-open"
        >
          <ExternalLink className="h-4 w-4" /> Ouvrir
        </button>
        <button
          onClick={handleOpen}
          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-5 py-2.5 rounded-lg font-semibold"
          data-testid="public-resource-download"
        >
          <Download className="h-4 w-4" /> Télécharger
        </button>
      </div>
      <p className="text-[11px] text-slate-400 max-w-md mx-auto">
        Si la ressource ne s'ouvre pas, contactez l'expéditeur. L'endpoint public de cette ressource doit être activé
        côté serveur pour ce type ({resourceType}).
      </p>
    </div>
  );
}


/**
 * ReplyForm — Mini-portail destinataire : permet de répondre à l'expéditeur
 * sans créer de compte. Le message est enregistré dans le registre RGPD
 * comme `transfer_type='reply_message'` lié au lien public d'origine.
 */
function ReplyForm({ token, senderEmailHint }) {
  const [open, setOpen] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!senderName || !senderEmail || !message) {
      setError('Tous les champs sont obligatoires');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(senderEmail)) {
      setError('Email invalide');
      return;
    }
    if (message.length > 4000) {
      setError('Message trop long (max 4000 caractères)');
      return;
    }
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/rgpd/public-link/reply/${encodeURIComponent(token)}`, {
        sender_name: senderName,
        sender_email: senderEmail,
        message,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-emerald-200" data-testid="reply-form-sent">
        <div className="p-6 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          <h3 className="font-bold text-slate-900">Message envoyé</h3>
          <p className="text-sm text-slate-600 mt-1">L'expéditeur a été notifié et pourra consulter votre réponse depuis son tableau de bord.</p>
          <p className="text-[11px] text-slate-400 mt-3"><Shield className="inline h-3 w-3 mr-0.5" />Réponse enregistrée au registre RGPD pour traçabilité.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200" data-testid="reply-form">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full p-5 flex items-center justify-between hover:bg-slate-50"
        data-testid="reply-form-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="text-left">
            <div className="font-semibold text-slate-900">Répondre à l'expéditeur</div>
            <div className="text-xs text-slate-500">Sans création de compte — réponse traçable RGPD</div>
          </div>
        </div>
        <span className="text-slate-400 text-xs">{open ? 'Réduire' : 'Ouvrir'}</span>
      </button>

      {open && (
        <form onSubmit={submit} noValidate className="border-t border-slate-100 p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-semibold mb-1 block">Votre nom</label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                maxLength={120}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                placeholder="Jean Dupont"
                required
                data-testid="reply-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-semibold mb-1 block">Votre email</label>
              <input
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                maxLength={200}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                placeholder="jean@exemple.fr"
                required
                data-testid="reply-email-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600 font-semibold mb-1 block flex justify-between">
              <span>Votre message</span>
              <span className="text-slate-400">{message.length} / 4000</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              maxLength={4000}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none resize-none"
              placeholder={`Bonjour ${senderEmailHint ? senderEmailHint : ''}, j'ai bien reçu le document...`}
              required
              data-testid="reply-message-input"
            />
          </div>

          {error && (
            <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2" data-testid="reply-error">
              <AlertTriangle className="inline h-3 w-3 mr-1" />{error}
            </div>
          )}

          <div className="text-[10px] text-slate-400 bg-slate-50 rounded p-2">
            <Shield className="inline h-2.5 w-2.5 mr-1" />
            Votre message sera enregistré au registre RGPD du site (Articles 30 & 32). Aucun compte ne sera créé.
            Vos données seront conservées 1 an puis automatiquement anonymisées.
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
            data-testid="reply-submit-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Envoi en cours...' : 'Envoyer la réponse'}
          </button>
        </form>
      )}
    </div>
  );
}
